/**
 * What has changed on the Location Tracking board since the person last looked, and where it goes.
 *
 * Andrew, 2026-09-29: "We flash a bar under the clock but don't bring the updated card to the top.
 * It does flash but if it's not on the screen the user can't see it." A change used to flash in
 * place for 1.5 s and keep an outline for 5 s more — on a column of fifteen cards, usually below
 * the fold, and gone before anyone scrolled to it. It also never said WHAT changed.
 *
 * So an update now:
 *  - rises to the top of its location's column, newest first, just under that location's clock;
 *  - STAYS marked until it is seen (the card is clicked, its column is marked, or Mark all seen) —
 *    not until a timer runs out;
 *  - says what changed, from the value it had when it was last seen to the value it has now. Two
 *    refreshes of progress before anyone looks read as one change, "Tasks 3 → 7", not two.
 *
 * Pure functions over plain data, so the rules are testable without rendering the board.
 */
import type { RecoveryObject } from '../../api'

export const objKey = (o: { locationId: number; id: string }) => `${o.locationId}:${o.id}`

export type Change =
    | { kind: 'tasks'; from: number; to: number; total: number }
    | { kind: 'engineer'; from: string | null; to: string | null }
    | { kind: 'status'; from: string | null; to: string | null }
    | { kind: 'issue'; count: number }
    | { kind: 'new' }

export interface Update {
    /** The object as it was when last seen: every change is measured from here. */
    baseline: RecoveryObject | null
    /** When the latest change arrived (ms). Newest first within a column. */
    at: number
}

/** The fields a person watches. Anything else changing is not an update worth raising. */
function differs(a: RecoveryObject, b: RecoveryObject): boolean {
    return a.engineer !== b.engineer
        || a.status !== b.status
        || a.tasksCompleted !== b.tasksCompleted
        || a.tasksIssue !== b.tasksIssue
}

/**
 * Fold one refresh into the unseen updates. `prev` is the previous refresh; the first load passes
 * null and raises nothing — everything on screen at the start is simply the board.
 */
export function applyRefresh(
    updates: ReadonlyMap<string, Update>,
    prev: readonly RecoveryObject[] | null,
    next: readonly RecoveryObject[],
    now: number,
): Map<string, Update> {
    const out = new Map(updates)
    if (prev === null) return out
    const before = new Map(prev.map((o) => [objKey(o), o]))
    for (const o of next) {
        const key = objKey(o)
        const old = before.get(key)
        if (old && !differs(old, o)) continue
        const existing = out.get(key)
        // The baseline is kept from the FIRST unseen change, so later ones accumulate onto it.
        out.set(key, { baseline: existing ? existing.baseline : (old ?? null), at: now })
    }
    return out
}

/** What changed between the last-seen state and now, in the order a person reads a card. */
export function describeChanges(update: Update, current: RecoveryObject): Change[] {
    const b = update.baseline
    if (!b) return [{ kind: 'new' }]
    const changes: Change[] = []
    if ((b.tasksIssue || 0) < (current.tasksIssue || 0)) {
        changes.push({ kind: 'issue', count: (current.tasksIssue || 0) - (b.tasksIssue || 0) })
    }
    if (b.status !== current.status) changes.push({ kind: 'status', from: b.status, to: current.status })
    if (b.engineer !== current.engineer) changes.push({ kind: 'engineer', from: b.engineer, to: current.engineer })
    if ((b.tasksCompleted || 0) !== (current.tasksCompleted || 0)) {
        changes.push({ kind: 'tasks', from: b.tasksCompleted || 0, to: current.tasksCompleted || 0, total: current.tasksTotal || 0 })
    }
    return changes
}

/** Updated cards first, newest update first; the rest keep the order the server sent them in. */
export function orderColumn(objects: readonly RecoveryObject[], updates: ReadonlyMap<string, Update>): RecoveryObject[] {
    const rank = new Map(objects.map((o, i) => [objKey(o), i]))
    return [...objects].sort((a, b) => {
        const ua = updates.get(objKey(a))
        const ub = updates.get(objKey(b))
        if (ua && ub) return ub.at - ua.at || rank.get(objKey(a))! - rank.get(objKey(b))!
        if (ua) return -1
        if (ub) return 1
        return rank.get(objKey(a))! - rank.get(objKey(b))!
    })
}

/** An update that has been undone by later data (a count back where it was) is no longer one. */
export function stillChanged(update: Update, current: RecoveryObject): boolean {
    return !update.baseline || differs(update.baseline, current)
}
